import React from 'react';
import { Grid, Paper, TextField, Typography } from '@mui/material';

import AppForm from '@/__core/components/AppForm';
import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';

import { InventoryStockTransactionFormData } from '../types';

interface StockTransactionFormProps {
  formData: InventoryStockTransactionFormData;
  companyDisabled: boolean;
  companyOptions: Array<{ company_id: string; company_name: string }>;
  onChangeForm: (updates: Partial<InventoryStockTransactionFormData>) => void;
  onSave: () => void;
  onCancel?: () => void;
}

const transactionTypeLabel = (value?: string) => {
  switch ((value || '').toLowerCase()) {
    case 'receipt':
      return 'Nhập kho';
    case 'issue':
      return 'Xuất kho';
    case 'return':
      return 'Hoàn trả';
    case 'adjustment':
      return 'Điều chỉnh';
    default:
      return value || '';
  }
};

const formatDateTimeVN = (isoString?: string) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export default function StockTransactionForm({
  formData,
  onSave,
  onCancel = () => {},
}: StockTransactionFormProps) {
  void onSave;

  const systemInfo = [
    formData.company_name ? `Công ty: ${formData.company_name}` : '',
    formData.reference_no ? `Số tham chiếu: ${formData.reference_no}` : '',
    formData.document_id ? `ID chứng từ: ${formData.document_id}` : '',
    formData.posted_at ? `Ghi sổ lúc: ${formatDateTimeVN(formData.posted_at)}` : '',
    formData.updated_at ? `Cập nhật hệ thống: ${formatDateTimeVN(formData.updated_at)}` : '',
  ].filter(Boolean).join('\n');

  return (
    <AppForm
      onSubmit={onSave}
      onCancel={onCancel}
      submitDisabled
      showSubmitButton={false}
      containerSx={{
        m: 0,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Số tham chiếu' labelWidth={100}>
              <TextField size='small' value={formData.reference_no} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Loại GD' labelWidth={100}>
              <TextField size='small' value={transactionTypeLabel(formData.txn_type)} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Loại chứng từ' labelWidth={100}>
              <TextField size='small' value={formData.document_type} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Mã vật tư' labelWidth={100}>
              <TextField size='small' value={formData.item_code} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Tên vật tư' labelWidth={100}>
              <TextField size='small' value={formData.item_name} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Công ty' labelWidth={100}>
              <TextField size='small' value={formData.company_name} disabled fullWidth />
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Mã kho' labelWidth={100}>
              <TextField size='small' value={formData.store_code} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Tên kho' labelWidth={100}>
              <TextField size='small' value={formData.store_name} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Lô hàng' labelWidth={100}>
              <TextField size='small' value={formData.lot_no} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Vị trí kệ' labelWidth={100}>
              <TextField size='small' value={formData.bin_location} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Ghi sổ lúc' labelWidth={100}>
              <TextField size='small' value={formatDateTimeVN(formData.posted_at)} disabled fullWidth />
            </LabeledField>
            <LabeledField label='ID chứng từ' labelWidth={100}>
              <TextField size='small' value={formData.document_id} disabled fullWidth />
            </LabeledField>

            <Grid container rowSpacing={1} columnSpacing={5}>
              <Grid size={{ xs: 12, md: 4 }}>
                <LabeledField label='SL biến động' labelWidth={100}>
                  <TextField type='number' size='small' value={formData.quantity_delta} disabled fullWidth />
                </LabeledField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <LabeledField label='Đơn giá' labelWidth={80}>
                  <TextField type='number' size='small' value={formData.unit_price} disabled fullWidth />
                </LabeledField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <LabeledField label='Giá trị dòng' labelWidth={80}>
                  <TextField type='number' size='small' value={formData.line_amount} disabled fullWidth />
                </LabeledField>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Grid container rowSpacing={1} sx={sharedFormStyles.additionalTextGrid}>
          <Grid size={{ xs: 12 }}>
            <LabeledField label='' labelWidth={0} alignItems='start'>
              <Typography
                component='div'
                sx={{
                  minHeight: 88,
                  width: '100%',
                  whiteSpace: 'pre-line',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: '#374151',
                  p: '10px 12px',
                  boxSizing: 'border-box',
                }}
              >
                {systemInfo || 'Giao dịch kho được sinh tự động từ chứng từ đã ghi sổ.'}
              </Typography>
            </LabeledField>
          </Grid>
        </Grid>
      </Paper>
    </AppForm>
  );
}
